import { Controller } from '@hotwired/stimulus'

interface PopoverEvent extends CustomEvent {
  detail: {
    source: HTMLElement
  }
}

export class PopoverController extends Controller {
  declare trigger: HTMLButtonElement | null
  declare content: HTMLElement | null

  connect(): void {
    this.trigger = this.element.querySelector(':scope > button')
    this.content = this.element.querySelector(':scope > [data-popover]')

    if (!this.trigger || !this.content) {
      const missing = []
      if (!this.trigger) missing.push('trigger')
      if (!this.content) missing.push('content')
      console.error(
        `Popover initialisation failed. Missing element(s): ${missing.join(', ')}`,
        this.element
      )
      return
    }

    this.trigger.addEventListener('click', () => {
      const isExpanded = this.trigger!.getAttribute('aria-expanded') === 'true'
      if (isExpanded) {
        this.closePopover()
      } else {
        this.openPopover()
      }
    })

    this.element.addEventListener('keydown', event => {
      if ((event as KeyboardEvent).key === 'Escape') {
        this.closePopover()
      }
    })

    document.addEventListener('click', event => {
      if (!this.element.contains(event.target as Node)) {
        this.closePopover()
      }
    })

    document.addEventListener('basecoat:popover', event => {
      if ((event as PopoverEvent).detail.source !== this.element) {
        this.closePopover(false)
      }
    })

    document.addEventListener('turbo:before-cache', () => {
      this.closePopover(false)
    })
  }

  closePopover = (focusOnTrigger = true) => {
    if (this.trigger?.getAttribute('aria-expanded') === 'false') return
    this.trigger?.setAttribute('aria-expanded', 'false')
    this.content?.setAttribute('aria-hidden', 'true')
    if (focusOnTrigger) {
      this.trigger?.focus()
    }
  }

  openPopover = () => {
    document.dispatchEvent(
      new CustomEvent('basecoat:popover', {
        detail: { source: this.element },
      })
    )

    const elementToFocus = this.content?.querySelector(
      '[autofocus]'
    ) as HTMLElement | null

    if (elementToFocus) {
      this.content?.addEventListener(
        'transitionend',
        () => {
          elementToFocus?.focus()
        },
        { once: true }
      )
    }

    this.trigger?.setAttribute('aria-expanded', 'true')
    this.content?.setAttribute('aria-hidden', 'false')
  }
}
